import { defaultSubmenuData, SubmenuData } from "frontend/canvas-designer-new/canvas-submenu";
import { PosScale } from "frontend/konva-stage-types";
import { CanvasUtils } from "frontend/utils/canvas-utils";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { ChartsFullDataLoadedState } from "shared/integrations/integration";
import UndoRedoStack from "utils/undo-redo/undo-redo-stack";

export const documentIdAtom = atom<{ documentId: string }>({ documentId: "" });

export const posScaleAtom = atom<PosScale>({ x: 0, y: 0, scale: 1 });

export const submenuDataAtom = atom<SubmenuData>(defaultSubmenuData);

export const isDraggingElementAtom = atom<boolean>(false);

export const editingElementIdAtom = atom<string | null>(null);

export const selectedElementIdsAtom = atom<string[]>([]);

export const internalSelectionAtom = atom<string[]>([]);

interface CellKey {
  col: string;
  row: string;
}
export interface TableSelectionState {
  id: string; // table id
  anchor: { col: string; row: string }; // "anchor" for when doing rect-selection
  cells: CellKey[]; // keys of the cells selected. last one is the "active" cell by convention
  // I wish javascript had Set that uses deep-object-equality instead of reference equality :(
}
export const tableSelectionAtom = atom<TableSelectionState | null>(null);

const allHighlightedElementIdsPerContainerAtom = atom<{ [containerId: string]: string[] }>({});

export const highlightedElementIdsPerContainerAtom = (containerId: string) =>
  atom(
    (get) => get(allHighlightedElementIdsPerContainerAtom)[containerId],
    (get, set, update: string[]) => {
      const allHighlightedElementIds = get(allHighlightedElementIdsPerContainerAtom);
      set(allHighlightedElementIdsPerContainerAtom, { ...allHighlightedElementIds, [containerId]: update });
    }
  );

// all element ids that are highlighted
export const highlightedElementIdsAtom = atom((get) => {
  const allHighlightedElementIds = get(allHighlightedElementIdsPerContainerAtom);
  return Object.values(allHighlightedElementIds).flat();
});

export const isPreviewExportModeAtom = atom<boolean>(false);

export const isThumbnailExportModeAtom = atom<boolean>(false);

export const isExportingAtom = atom<boolean>(false);

export const isStageReadyAtom = atom<boolean>(false);

export const editingElementLinkAtom = atom<string>("");

export const utilsAtom = atomFamily((_documentId: string | null | undefined) => atom<CanvasUtils | null>(null));

export const isAnyKindOfExportAtom = atom<boolean>(
  (get) => get(isPreviewExportModeAtom) || get(isThumbnailExportModeAtom) || get(isExportingAtom)
);

export const canvasTooltipAtom = atom<{ label: string; relativeTo: any } | null>(null); // relativeTo should be a konva node

export const mondayIntegrationPopupAtom = atom<{
  isOpen: boolean;
  selectedResult?: "map" | "cancel" | undefined;
  integrationId?: string;
}>({
  isOpen: false,
});

export const mouseStateAtom = atom<"up" | "down" | "unknown">("unknown");

export const smartOrgChartsIsFullyLoadedAtom = atom<ChartsFullDataLoadedState>({});

export const undoRedoStackAtom = atom<UndoRedoStack>(new UndoRedoStack());

export const showTemplateCreationLoaderModalAtom = atom<boolean>(false);

export const imageCacheAtom = atom<Record<string, HTMLImageElement>>({});
export const connectorTransformingAtom = atom<boolean>(false);

export interface TemplatePreviewBoundaries {
  topSelector: string | null;
  bottomSelector: string | null;
  defaultTopOffset?: number;
  defaultBottomOffset?: number;
}

export const hoveredTemplatePreviewAtom = atom<{
  templateId: string;
  templateData: Record<string, any>;
  boundaries?: TemplatePreviewBoundaries;
  ghostTransform?: {
    position: { x: number; y: number };
    offset: { x: number; y: number };
    scale: number;
  };
} | null>(null);
